import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../utils/helpers/locale";

// Read-only view of one generation run: where it is, how long it has taken,
// what the solver did, and — for a run that did not fully succeed — why.
// Purely presentational: the page owns fetching and polling.

// Statuses that still occupy the school's single generation slot. Mirrors
// TimetableRun.IN_FLIGHT_STATUSES on the backend.
const IN_FLIGHT = new Set(["queued", "running"]);
// Statuses a manager may review and commit from (TimetableRun.REVIEWABLE_STATUSES).
const REVIEWABLE = new Set(["succeeded", "partial"]);

export const isRunInFlight = (status) => IN_FLIGHT.has(status);
export const isRunReviewable = (status) => REVIEWABLE.has(status);

// One tint per status — a table rather than nested ternaries, so a new status
// is one row here (same convention as ScheduleImport's OUTCOME_CLASS).
export const RUN_STATUS_CLASS = {
  queued: "bg-slate-100 text-slate-600",
  running: "bg-indigo-50 text-indigo-600",
  succeeded: "bg-emerald-50 text-emerald-700",
  partial: "bg-amber-50 text-amber-700",
  infeasible: "bg-rose-50 text-rose-700",
  failed: "bg-rose-50 text-rose-700",
  cancelled: "bg-slate-100 text-slate-500",
  committed: "bg-emerald-600 text-white",
};

// Solver stats we know how to label, in reading order. Anything else the solver
// starts reporting is still shown (humanized key), never as a JSON blob.
const STAT_ORDER = ["cards", "slots", "wall_time", "branches", "conflicts", "stage"];
const STAT_LABEL_KEY = {
  cards: "timetable.statCards",
  slots: "timetable.statSlots",
  wall_time: "timetable.statWallTime",
  branches: "timetable.statBranches",
  conflicts: "timetable.statConflicts",
  stage: "timetable.statStage",
};
// Internal bookkeeping that would be noise in the stat grid: `budget` and
// `improvements` are already rendered as prose above it, and `budget_detail` is
// a nested object kept for offline tuning of the budget coefficients.
const STAT_HIDDEN = new Set(["budget", "budget_detail", "improvements"]);

// Stop reasons that mean "there was more to find" — these earn a Keep looking
// button, because the honest answer to "is this the best week?" is "no".
export const RESUMABLE_STOPS = new Set(["stagnation", "wall_clock", "user_stop"]);

const humanizeKey = (key) => key.replace(/_/g, " ");

// `t` is threaded in rather than read from a hook: these stay plain
// functions, and every call site is inside a component that already has it.
const formatStat = (key, value, t) => {
  if (key === "wall_time" && typeof value === "number") {
    // Not toFixed(): that hardcodes a "." decimal separator, which is wrong in
    // both ru and kk, and the trailing "s" is an English unit.
    return t("timetable.durationSec", {
      seconds: formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    });
  }
  if (typeof value === "number") return formatNumber(value);
  return String(value);
};

const msBetween = (from, to) => {
  const start = from ? new Date(from).getTime() : NaN;
  if (Number.isNaN(start) || !to || Number.isNaN(to)) return null;
  return Math.max(0, to - start);
};

const formatDuration = (ms, t) => {
  const total = Math.round(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  // Rendered under the translated t("timetable.elapsed") label, so "3m 20s"
  // next to a Kazakh heading read as a bug.
  return minutes > 0
    ? t("timetable.durationMinSec", { minutes, seconds })
    : t("timetable.durationSec", { seconds });
};

export default function RunStatusPanel({ run, constraintLabels = {} }) {
  const { t } = useTranslation();
  const inFlight = isRunInFlight(run?.status);

  // Re-render once a second only while the run is actually moving; a finished
  // run's elapsed time is fixed, so no timer is left behind.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!inFlight) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [inFlight, run?.uuid]);

  const stats = useMemo(() => {
    const raw = run?.solver_stats;
    if (!raw || typeof raw !== "object") return [];
    const known = STAT_ORDER.filter((key) => raw[key] !== undefined && raw[key] !== null);
    const extra = Object.keys(raw).filter(
      (key) =>
        !STAT_ORDER.includes(key) &&
        !STAT_HIDDEN.has(key) &&
        ["number", "string", "boolean"].includes(typeof raw[key]),
    );
    return [...known, ...extra].map((key) => ({
      key,
      label: STAT_LABEL_KEY[key] ? t(STAT_LABEL_KEY[key]) : humanizeKey(key),
      value: formatStat(key, raw[key], t),
    }));
  }, [run?.solver_stats, t]);

  // The tray, summarised: how many cards did not land, and the distinct reasons
  // the solver gave. `placements` is only present on the detail endpoint.
  const board = useMemo(() => {
    const placements = Array.isArray(run?.placements) ? run.placements : null;
    if (!placements || placements.length === 0) return null;
    const unplaced = placements.filter((p) => p.weekday === null || p.weekday === undefined);
    const reasons = new Map();
    unplaced.forEach((p) => {
      const reason = (p.unplaced_reason || "").trim();
      if (!reason) return;
      reasons.set(reason, (reasons.get(reason) || 0) + 1);
    });
    return {
      total: placements.length,
      placed: placements.length - unplaced.length,
      unplaced: unplaced.length,
      reasons: [...reasons.entries()].map(([reason, count]) => ({ reason, count })),
    };
  }, [run?.placements]);

  if (!run) return null;

  const diagnostics = run.diagnostics && typeof run.diagnostics === "object" ? run.diagnostics : {};
  const precheck = Array.isArray(diagnostics.precheck) ? diagnostics.precheck : [];
  const core = Array.isArray(diagnostics.core) ? diagnostics.core : [];
  const warnings = Array.isArray(diagnostics.warnings) ? diagnostics.warnings : [];

  const elapsedMs = msBetween(
    run.started_at || run.created_at,
    inFlight ? now : run.finished_at ? new Date(run.finished_at).getTime() : null,
  );

  const statusClass = RUN_STATUS_CLASS[run.status] || "bg-slate-100 text-slate-600";

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="p-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2.5">
            <span
              className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${statusClass}`}
            >
              {t(`timetable.status.${run.status}`, run.status)}
            </span>
            {inFlight && (
              <span
                aria-hidden="true"
                className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"
              />
            )}
          </div>
          <p className="mt-2 text-sm font-bold text-slate-700">
            {t(`timetable.statusHint.${run.status}`, "")}
          </p>
        </div>
        <dl className="flex items-center gap-6">
          {elapsedMs !== null && (
            <div>
              <dt className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                {t("timetable.elapsed")}
              </dt>
              <dd className="text-lg font-black text-slate-900 tabular-nums">
                {formatDuration(elapsedMs, t)}
              </dd>
            </div>
          )}
          {run.objective_value !== null && run.objective_value !== undefined && (
            <div>
              <dt className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                {t("timetable.objective")}
              </dt>
              <dd className="text-lg font-black text-slate-900 tabular-nums">
                {formatNumber(run.objective_value)}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* While the run is in flight there are no placements yet — the board is
          bulk-created once at the end so the 2s poll stays cheap — so this
          reads `run.progress`, which the solver's watchdog rewrites every ~2s.
          Before this existed the entire wait was a spinner and an elapsed
          counter with no denominator. */}
      {inFlight && <LiveProgress progress={run.progress} />}

      {!inFlight && run.stop_reason && (
        <StopReasonNote run={run} elapsedMs={elapsedMs} />
      )}

      {board && (
        <div className="px-6 pb-5">
          <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 mb-1.5">
            <span>{t("timetable.cardsPlaced", { placed: board.placed, total: board.total })}</span>
            {board.unplaced > 0 && (
              <span className="text-amber-600">
                {t("timetable.cardsInTray", { count: board.unplaced })}
              </span>
            )}
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full rounded-full ${board.unplaced > 0 ? "bg-amber-400" : "bg-emerald-500"}`}
              style={{ width: `${board.total ? (board.placed / board.total) * 100 : 0}%` }}
            />
          </div>
          {board.reasons.length > 0 && (
            <ul className="mt-3 space-y-1">
              {board.reasons.map((row) => (
                <li key={row.reason} className="text-[11px] font-medium text-slate-500">
                  <span className="font-black text-amber-600 tabular-nums mr-1.5">×{row.count}</span>
                  {row.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {stats.length > 0 && (
        <div className="px-6 pb-6">
          <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {stats.map((stat) => (
              <div key={stat.key} className="rounded-2xl bg-slate-50 border border-slate-100 px-3 py-2.5">
                <dt className="text-[9px] font-black uppercase tracking-widest text-slate-500 truncate">
                  {stat.label}
                </dt>
                <dd className="text-sm font-black text-slate-800 tabular-nums truncate">{stat.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {run.error && (
        <div className="mx-6 mb-6 rounded-2xl bg-rose-50 border border-rose-100 p-4" role="alert">
          <p className="text-[10px] font-black uppercase tracking-widest text-rose-500">
            {t("timetable.runError")}
          </p>
          <p className="mt-1 text-xs font-bold text-rose-700 leading-relaxed break-words">{run.error}</p>
        </div>
      )}

      {precheck.length > 0 && (
        <div className="mx-6 mb-6 rounded-2xl bg-amber-50 border border-amber-100 p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">
            {t("timetable.precheckTitle")}
          </p>
          <p className="mt-1 text-[11px] font-medium text-amber-700/80">
            {t("timetable.precheckHint")}
          </p>
          <ul className="mt-3 space-y-2">
            {precheck.map((finding, index) => (
              <li
                key={`${finding.code || "finding"}-${index}`}
                className="text-xs font-bold text-amber-800 leading-relaxed flex gap-2"
              >
                <span aria-hidden="true" className="text-amber-400 shrink-0">
                  •
                </span>
                <span>{finding.message || finding.code}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {core.length > 0 && (
        <div className="mx-6 mb-6 rounded-2xl bg-rose-50 border border-rose-100 p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-rose-500">
            {t("timetable.coreTitle")}
          </p>
          <p className="mt-1 text-[11px] font-medium text-rose-700/80">{t("timetable.coreHint")}</p>
          <ul className="mt-3 space-y-2">
            {core.map((item) => {
              const ref = String(item.ref ?? "");
              const isLock = item.kind === "lock" || ref.startsWith("pin:");
              const label = isLock
                ? t("timetable.coreLock", { card: ref.replace(/^pin:/, "") })
                : constraintLabels[ref] || t("timetable.coreRule", { ref: ref.slice(0, 8) });
              return (
                <li key={ref} className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest ${
                      isLock ? "bg-slate-200 text-slate-600" : "bg-rose-100 text-rose-600"
                    }`}
                  >
                    {isLock ? t("timetable.coreKindLock") : t("timetable.coreKindRule")}
                  </span>
                  <span className="text-xs font-bold text-rose-800">{label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="mx-6 mb-6 rounded-2xl bg-slate-50 border border-slate-200 p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
            {t("timetable.warningsTitle")}
          </p>
          <ul className="mt-2 space-y-1.5">
            {warnings.map((warning, index) => (
              <li key={`${index}-${warning}`} className="text-[11px] font-medium text-slate-600 leading-relaxed">
                {String(warning)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

// What the solver is doing right now. Deliberately TWO different indicators,
// because the two stages are honestly different kinds of progress:
//
//   Phase 1 "fitting every lesson in" — genuinely determinate. The stage-1
//     objective IS the unplaced lesson time, so `1 - unplaced/total` is a real
//     bar. It usually snaps rather than creeps (measured: 840 cards went 840 ->
//     0 unplaced in one 0.65s step), but on an over-subscribed school it stalls
//     partway and THAT IS THE ANSWER — those periods genuinely do not fit.
//
//   Phase 2 "making the week better" — placement is fixed, so a percentage
//     would be a lie. There is no meaningful denominator: the objective's best
//     bound is measurably pinned at 0 for the whole stage (the soft penalty is
//     a sum of non-negative terms, so 0 is always a valid bound), which is why
//     no "% converged" is shown here and none should be added.
function LiveProgress({ progress }) {
  const { t } = useTranslation();
  if (!progress || typeof progress !== "object" || !progress.phase) return null;

  const isPlacing = progress.phase === "placing";
  const total = Number(progress.periods_total) || 0;
  const unplaced = Number(progress.periods_unplaced) || 0;
  const placedPct = total > 0 ? Math.round(((total - unplaced) / total) * 100) : 0;
  const quietFor = Math.max(
    0,
    Math.round((Number(progress.elapsed) || 0) - (Number(progress.last_improvement_at) || 0)),
  );

  return (
    <div className="px-6 pb-5">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
        <p className="text-xs font-black text-slate-700">
          {t(isPlacing ? "timetable.progress.placing" : "timetable.progress.polishing")}
        </p>
        <p className="text-[11px] font-bold text-slate-500 tabular-nums">
          {t("timetable.progress.ofBudget", {
            elapsed: Math.round(Number(progress.elapsed) || 0),
            budget: Number(progress.budget) || 0,
          })}
        </p>
      </div>

      {isPlacing ? (
        <>
          <div
            role="progressbar"
            aria-valuenow={placedPct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t("timetable.progress.placing")}
            className="h-2 rounded-full bg-slate-100 overflow-hidden"
          >
            <div
              className="h-full rounded-full bg-indigo-500 transition-[width] duration-500"
              style={{ width: `${placedPct}%` }}
            />
          </div>
          <p className="mt-2 text-[11px] font-medium text-slate-500 tabular-nums">
            {t("timetable.progress.periodsPlaced", {
              placed: total - unplaced,
              total,
            })}
          </p>
        </>
      ) : (
        // No bar: there is no honest denominator for this stage. Prose in a
        // polite live region instead — a progressbar with no value is worse for
        // assistive tech than a sentence that says what is happening.
        <div
          aria-live="polite"
          className="rounded-xl bg-indigo-50/70 border border-indigo-100 px-4 py-3"
        >
          <p className="text-[11px] font-bold text-indigo-800 tabular-nums">
            {t("timetable.progress.improvements", {
              count: Number(progress.improvements) || 0,
            })}
          </p>
          <p className="mt-1 text-[11px] font-medium text-indigo-500 tabular-nums">
            {quietFor > 0
              ? t("timetable.progress.quietFor", {
                  seconds: quietFor,
                  patience: Number(progress.patience) || 0,
                })
              : t("timetable.progress.stillImproving")}
          </p>
        </div>
      )}
    </div>
  );
}

// Why the search ended, in words. This is not decoration: CP-SAT has no
// "interrupted" status, so a search stopped after 3s of a 5-minute budget comes
// back as OPTIMAL or FEASIBLE exactly like a proven-best one. Without this the
// manager is shown a green board and invited to commit it over the live grid.
function StopReasonNote({ run, elapsedMs }) {
  const { t } = useTranslation();
  const seconds = elapsedMs === null ? null : Math.round(elapsedMs / 1000);
  const proven = run.stop_reason === "proved_optimal";
  return (
    <div className="px-6 pb-5">
      <div
        className={`rounded-2xl border px-4 py-3 ${
          proven
            ? "bg-emerald-50 border-emerald-100"
            : "bg-slate-50 border-slate-200"
        }`}
      >
        <p
          className={`text-[11px] font-bold leading-relaxed ${
            proven ? "text-emerald-800" : "text-slate-600"
          }`}
        >
          {t(`timetable.stopReason.${run.stop_reason}`, {
            defaultValue: "",
            seconds: seconds ?? 0,
            budget: run.budget_seconds ?? 0,
          })}
        </p>
      </div>
    </div>
  );
}
